import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const createApiInstance = (token, timeout = 30000) => {
  return axios.create({
    baseURL: `/api`,
    timeout,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

export const api = {
  // Restaurants
  getRestaurants: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/restaurants');
    return response.data;
  },
  
  getRestaurant: async (token, id) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/restaurants/${id}`);
    return response.data;
  },
  
  createRestaurant: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/restaurants', data);
    return response.data;
  },
  
  updateRestaurant: async (token, id, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/restaurants/${id}`, data);
    return response.data;
  },
  
  deleteRestaurant: async (token, id) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/restaurants/${id}`);
    return response.data;
  },

  searchRestaurantGoogle: async (token, query, city = '') => {
    const instance = createApiInstance(token);
    const response = await instance.post('/restaurants/search-google', { query, city });
    return response.data;
  },
  
  // Metrics
  getMetrics: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/metrics/${restaurantId}`);
    return response.data;
  },
  
  getMetricsSummary: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/metrics/summary/${restaurantId}`);
    return response.data;
  },
  
  createMetrics: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/metrics', data);
    return response.data;
  },
  updateMetrics: async (token, metricId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/metrics/${metricId}`, data);
    return response.data;
  },
  
  // Reviews
  getReviews: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/reviews/${restaurantId}`);
    return response.data;
  },
  
  getReviewsSummary: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/reviews/summary/${restaurantId}`);
    return response.data;
  },
  
  createReview: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/reviews', data);
    return response.data;
  },
  
  // AI Analysis
  analyzeWithAI: async (token, restaurantId, analysisType, language = 'it') => {
    const instance = createApiInstance(token, 120000);
    const response = await instance.post('/ai/analyze', {
      restaurant_id: restaurantId,
      analysis_type: analysisType,
      language: language
    });
    return response.data;
  },
  
  // Dashboard
  getDashboardOverview: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/dashboard/overview');
    return response.data;
  },
  
  getRestaurantComparison: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/dashboard/comparison');
    return response.data;
  },
  
  // Demo Data
  seedDemoData: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/seed-demo-data');
    return response.data;
  },

  // Health Score
  getHealthScore: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/health-score/${restaurantId}`);
    return response.data;
  },

  // Google Reviews Sync
  syncGoogleReviews: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/reviews/sync-google/${restaurantId}`);
    return response.data;
  },

  // Email Alert Test
  sendTestAlert: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/alerts/send-test/${restaurantId}`);
    return response.data;
  },

  // Team (Sub-users)
  getTeamMembers: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/team/members');
    return response.data;
  },
  createTeamMember: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/team/members', data);
    return response.data;
  },
  updateTeamMember: async (token, memberId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/team/members/${memberId}`, data);
    return response.data;
  },
  deleteTeamMember: async (token, memberId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/team/members/${memberId}`);
    return response.data;
  },

  // Excel Menu Import
  importMenuExcel: async (token, restaurantId, fileData) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/menu-items/import-excel-data/${restaurantId}`, { file_data: fileData });
    return response.data;
  },

  // Competitor Search
  searchCompetitors: async (token, restaurantId, query, radiusKm = 5) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/competitors/search-google/${restaurantId}`, { query, radius_km: radiusKm });
    return response.data;
  },

  // Menu Categories
  getMenuCategories: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/menu-categories/${restaurantId}`);
    return response.data;
  },

  createMenuCategory: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/menu-categories/${restaurantId}`, data);
    return response.data;
  },

  deleteMenuCategory: async (token, catId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/menu-categories/${catId}`);
    return response.data;
  },

  // Staff Categories
  getStaffCategories: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/staff-categories/${restaurantId}`);
    return response.data;
  },

  createStaffCategory: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/staff-categories/${restaurantId}`, data);
    return response.data;
  },

  deleteStaffCategory: async (token, catId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/staff-categories/${catId}`);
    return response.data;
  },

  // Menu Items
  getMenuItems: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/menu-items/${restaurantId}`);
    return response.data;
  },

  getMenuSummary: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/menu-items/summary/${restaurantId}`);
    return response.data;
  },

  getMenuAudit: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/menu-audit/${restaurantId}`);
    return response.data;
  },

  createMenuItem: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/menu-items', data);
    return response.data;
  },

  updateMenuItem: async (token, itemId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/menu-items/${itemId}`, data);
    return response.data;
  },

  deleteMenuItem: async (token, itemId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/menu-items/${itemId}`);
    return response.data;
  },

  // Export
  exportMetricsCsv: (token, restaurantId) => {
    return `/api/export/metrics/${restaurantId}?token=${token}`;
  },

  exportReviewsCsv: (token, restaurantId) => {
    return `/api/export/reviews/${restaurantId}?token=${token}`;
  },

  exportMenuCsv: (token, restaurantId) => {
    return `/api/export/menu/${restaurantId}?token=${token}`;
  },

  // Admin
  getAdminUsers: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/admin/users');
    return response.data;
  },

  getAdminStats: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/admin/stats');
    return response.data;
  },

  // Staff
  getStaff: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/staff/${restaurantId}`);
    return response.data;
  },

  getStaffSummary: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/staff/summary/${restaurantId}`);
    return response.data;
  },

  createStaffMember: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/staff', data);
    return response.data;
  },

  updateStaffMember: async (token, staffId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/staff/${staffId}`, data);
    return response.data;
  },

  deleteStaffMember: async (token, staffId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/staff/${staffId}`);
    return response.data;
  },

  // Expenses
  getExpenseCategories: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/expense-categories/${restaurantId}`);
    return response.data;
  },

  createExpenseCategory: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/expense-categories', data);
    return response.data;
  },

  getExpenses: async (token, restaurantId, month = null) => {
    const instance = createApiInstance(token);
    const url = month ? `/expenses/${restaurantId}?month=${month}` : `/expenses/${restaurantId}`;
    const response = await instance.get(url);
    return response.data;
  },

  getExpensesSummary: async (token, restaurantId, month = null) => {
    const instance = createApiInstance(token);
    const url = month ? `/expenses/summary/${restaurantId}?month=${month}` : `/expenses/summary/${restaurantId}`;
    const response = await instance.get(url);
    return response.data;
  },

  // Monthly Actuals (Consuntivo)
  getMonthlyActuals: async (token, restaurantId, month) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/monthly-actuals/${restaurantId}/${month}`);
    return response.data;
  },
  saveMonthlyActuals: async (token, restaurantId, month, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/monthly-actuals/${restaurantId}/${month}`, data);
    return response.data;
  },


  createExpense: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/expenses', data);
    return response.data;
  },

  deleteExpense: async (token, expenseId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/expenses/${expenseId}`);
    return response.data;
  },

  updateExpenseCategory: async (token, categoryId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/expense-categories/${categoryId}`, data);
    return response.data;
  },

  deleteExpenseCategory: async (token, categoryId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/expense-categories/${categoryId}`);
    return response.data;
  },

  updateExpense: async (token, expenseId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/expenses/${expenseId}`, data);
    return response.data;
  },

  // Alerts
  getAlertSettings: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/alert-settings');
    return response.data;
  },

  updateAlertSettings: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put('/alert-settings', data);
    return response.data;
  },

  getAlerts: async (token, unreadOnly = false) => {
    const instance = createApiInstance(token);
    const url = unreadOnly ? '/alerts?unread_only=true' : '/alerts';
    const response = await instance.get(url);
    return response.data;
  },

  markAlertRead: async (token, alertId) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/alerts/${alertId}/read`);
    return response.data;
  },

  markAllAlertsRead: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.put('/alerts/read-all');
    return response.data;
  },

  checkAlerts: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/check-alerts/${restaurantId}`);
    return response.data;
  },

  // Preferences & Translations
  getPreferences: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/preferences');
    return response.data;
  },

  updatePreferences: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put('/preferences', data);
    return response.data;
  },

  getTranslations: async (lang) => {
    const response = await axios.get(`/api/translations/${lang}`);
    return response.data;
  },

  // Integrations
  getIntegrationSettings: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/integrations/${restaurantId}`);
    return response.data;
  },

  updateIntegrationSettings: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/integrations/${restaurantId}`, data);
    return response.data;
  },

  importReview: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/reviews/import', data);
    return response.data;
  },

  importBulkReviews: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/reviews/import-bulk', data);
    return response.data;
  },

  importCsvReviews: async (token, restaurantId, source, csvContent) => {
    const instance = createApiInstance(token);
    const formData = new URLSearchParams();
    formData.append('restaurant_id', restaurantId);
    formData.append('source', source);
    formData.append('csv_content', csvContent);
    const response = await instance.post('/reviews/import-csv-text', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
    return response.data;
  },

  // Competitors
  getCompetitors: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/competitors/${restaurantId}`);
    return response.data;
  },

  createCompetitor: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/competitors', data);
    return response.data;
  },

  updateCompetitor: async (token, competitorId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/competitors/${competitorId}`, data);
    return response.data;
  },

  deleteCompetitor: async (token, competitorId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/competitors/${competitorId}`);
    return response.data;
  },

  // Competitor Prices
  getCompetitorPrices: async (token, competitorId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/competitor-prices/${competitorId}`);
    return response.data;
  },

  createCompetitorPrice: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/competitor-prices', data);
    return response.data;
  },

  updateCompetitorPrice: async (token, priceId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/competitor-prices/${priceId}`, data);
    return response.data;
  },

  deleteCompetitorPrice: async (token, priceId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/competitor-prices/${priceId}`);
    return response.data;
  },

  getCompetitorComparison: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/competitor-comparison/${restaurantId}`);
    return response.data;
  },

  // Stress Test / Viability Analysis
  runStressTest: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/restaurants/${restaurantId}/stress-test`, data);
    return response.data;
  },

  // Separate AI analysis for stress test (avoids proxy timeout)
  runStressTestAi: async (token, restaurantId, payload) => {
    const instance = createApiInstance(token, 120000); // 2 min timeout
    const response = await instance.post(`/restaurants/${restaurantId}/stress-test/ai`, payload);
    return response.data;
  },

  // Department Benchmarks
  getBenchmarks: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/restaurants/${restaurantId}/benchmarks`);
    return response.data;
  },
  saveBenchmarks: async (token, restaurantId, departments) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/restaurants/${restaurantId}/benchmarks`, {
      restaurant_id: restaurantId,
      departments
    });
    return response.data;
  },


  // Scenario Simulator
  runScenario: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/restaurants/${restaurantId}/scenario-simulator`, data);
    return response.data;
  },

  // Daily Income
  createDailyIncome: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/daily-income/${restaurantId}`, data);
    return response.data;
  },
  getDailyIncome: async (token, restaurantId, days = 30) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/daily-income/${restaurantId}?days=${days}`);
    return response.data;
  },
  deleteDailyIncome: async (token, entryId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/daily-income/${entryId}`);
    return response.data;
  },

  // Daily Performance
  getDailyPerformance: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/daily-performance/${restaurantId}`);
    return response.data;
  },

  // Market Benchmark
  getMarketBenchmark: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/market-benchmark/${restaurantId}`);
    return response.data;
  },

  // Platform Benchmark
  getPlatformBenchmark: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/benchmark-platform/${restaurantId}`);
    return response.data;
  },
  getPlatformBenchmarkSummary: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/benchmark-platform/summary/${restaurantId}`);
    return response.data;
  },

  // Menu Engineering
  getMenuEngineering: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/menu-engineering/${restaurantId}`);
    return response.data;
  },

  // Staff Optimizer
  getStaffOptimizer: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/staff-optimizer/${restaurantId}`, data);
    return response.data;
  },

  // Waste Tracker
  createWaste: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/waste/${restaurantId}`, data);
    return response.data;
  },
  getWaste: async (token, restaurantId, days = 30) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/waste/${restaurantId}?days=${days}`);
    return response.data;
  },
  deleteWaste: async (token, entryId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/waste/${entryId}`);
    return response.data;
  },
  getWasteSummary: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/waste/summary/${restaurantId}`);
    return response.data;
  },

  // Currency
  getCurrencies: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/currencies');
    return response.data;
  },
  getUserCurrency: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/user/currency');
    return response.data;
  },
  setUserCurrency: async (token, currency) => {
    const instance = createApiInstance(token);
    const response = await instance.put('/user/currency', { currency });
    return response.data;
  },

  // Month over month comparison
  getMonthComparison: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/month-comparison/${restaurantId}`);
    return response.data;
  },

  // Menu Audit
  runMenuAudit: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/menu-audit/${restaurantId}`);
    return response.data;
  },
  getMenuAudits: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/menu-audits/${restaurantId}`);
    return response.data;
  },
  generatePdfReport: async (token, restaurantId, reportType) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/report/${restaurantId}/${reportType}`, { responseType: 'blob' });
    return response.data;
  },

  // Expense Alerts
  getExpenseAlerts: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/expense-alerts/${restaurantId}`);
    return response.data;
  },

  // Area Analysis
  getAreaAnalysis: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/area-analysis/${restaurantId}`);
    return response.data;
  },

  // Auto Reports & Audit
  getAutoReportSettings: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/auto-report-settings/${restaurantId}`);
    return response.data;
  },
  updateAutoReportSettings: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/auto-report-settings/${restaurantId}`, data);
    return response.data;
  },
  updateAutoAuditSettings: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/auto-audit-settings/${restaurantId}`, data);
    return response.data;
  },
  getAutoReports: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/auto-reports/${restaurantId}`);
    return response.data;
  },
  generateAutoReport: async (token, restaurantId, reportType) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/auto-report-generate/${restaurantId}/${reportType}`);
    return response.data;
  },
  runAutoAudit: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/auto-audit-run/${restaurantId}`);
    return response.data;
  },
  getAutoAudits: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/auto-audits/${restaurantId}`);
    return response.data;
  },

  // Admin
  adminGetPlans: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/admin/plans');
    return response.data;
  },
  adminGetUsers: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/admin/users');
    return response.data;
  },
  adminUpdateUserPlan: async (token, userId, plan) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/admin/users/${userId}/plan`, { plan });
    return response.data;
  },
  adminUpdateUserRole: async (token, userId, role) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/admin/users/${userId}/role`, { role });
    return response.data;
  },
  adminDeleteUser: async (token, userId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/admin/users/${userId}`);
    return response.data;
  },
  adminGetStats: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/admin/stats');
    return response.data;
  },

  // Cash Flow
  getCashFlow: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/cash-flow/${restaurantId}`);
    return response.data;
  },

  // Advanced Menu Engineering
  getMenuEngineeringAdvanced: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/menu-engineering-advanced/${restaurantId}`);
    return response.data;
  },
  getMenuEngineeringAI: async (token, restaurantId, language = 'it') => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/menu-engineering-ai/${restaurantId}`, { params: { language } });
    return response.data;
  },

  // Advanced Staff Analysis
  getStaffAnalysis: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/staff-analysis/${restaurantId}`);
    return response.data;
  },

  // Cash Flow Simulator
  runCashFlowSimulator: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/cash-flow-simulator/${restaurantId}`, data);
    return response.data;
  },

  // Superadmin
  superadminGetStats: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/superadmin/stats');
    return response.data;
  },
  superadminGetUsers: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/superadmin/users');
    return response.data;
  },
  superadminToggleUser: async (token, userId) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/superadmin/users/${userId}/toggle`);
    return response.data;
  },
  superadminUpdatePlan: async (token, userId, plan) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/superadmin/users/${userId}/plan`, { plan });
    return response.data;
  },
  superadminResetPassword: async (token, userId, password) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/superadmin/users/${userId}/password`, { password });
    return response.data;
  },
  superadminGetCredentials: async (token, userId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/superadmin/users/${userId}/credentials`);
    return response.data;
  },
  superadminToggleRestaurant: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/superadmin/restaurants/${restaurantId}/toggle`);
    return response.data;
  },
  superadminCreateUser: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/superadmin/users', data);
    return response.data;
  },
  superadminTogglePaid: async (token, userId, isPaid) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/superadmin/users/${userId}/paid`, { is_paid: isPaid });
    return response.data;
  },
  superadminExtendTrial: async (token, userId, days) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/superadmin/users/${userId}/extend-trial`, { days });
    return response.data;
  },

  // Plan Management (Superadmin)
  getPublicPlans: async () => {
    const instance = createApiInstance();
    const response = await instance.get('/public/plans');
    return response.data;
  },
  superadminGetPlans: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/superadmin/plans');
    return response.data;
  },
  superadminUpdatePlanConfig: async (token, planId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/superadmin/plans/${planId}`, data);
    return response.data;
  },
  superadminCreatePlan: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/superadmin/plans', data);
    return response.data;
  },
  superadminDeletePlan: async (token, planId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/superadmin/plans/${planId}`);
    return response.data;
  },
  superadminReorderPlans: async (token, planIds) => {
    const instance = createApiInstance(token);
    const response = await instance.put('/superadmin/plans-reorder', { plan_ids: planIds });
    return response.data;
  },
  superadminGetFeatureKeys: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/superadmin/feature-keys');
    return response.data;
  },
  superadminGetSettings: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/superadmin/settings');
    return response.data;
  },
  superadminUpdateSettings: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put('/superadmin/settings', data);
    return response.data;
  },

  // Lead Management (SuperAdmin)
  superadminGetLeads: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/leads/admin/list');
    return response.data;
  },
  superadminGetLeadStats: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/leads/admin/stats');
    return response.data;
  },
  superadminDeleteLead: async (token, email) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/leads/admin/${encodeURIComponent(email)}`);
    return response.data;
  },
  superadminResendLeadEmail: async (token, email, leadType) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/leads/admin/resend-email/${encodeURIComponent(email)}/${leadType}`);
    return response.data;
  },
  superadminExportLeadsCsv: (token) => {
    return `/api/leads/admin/export-csv?token=${token}`;
  },

  // Food Cost Management - Suppliers
  getSuppliers: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/food-cost/suppliers/${restaurantId}`);
    return response.data;
  },
  createSupplier: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/food-cost/suppliers', data);
    return response.data;
  },
  updateSupplier: async (token, supplierId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/food-cost/suppliers/${supplierId}`, data);
    return response.data;
  },
  deleteSupplier: async (token, supplierId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/food-cost/suppliers/${supplierId}`);
    return response.data;
  },

  // Food Cost Management - Ingredients
  getIngredients: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/food-cost/ingredients/${restaurantId}`);
    return response.data;
  },
  createIngredient: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/food-cost/ingredients', data);
    return response.data;
  },
  updateIngredient: async (token, ingredientId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/food-cost/ingredients/${ingredientId}`, data);
    return response.data;
  },
  deleteIngredient: async (token, ingredientId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/food-cost/ingredients/${ingredientId}`);
    return response.data;
  },

  // Food Cost Management - Recipes
  getRecipes: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/food-cost/recipes/${restaurantId}`);
    return response.data;
  },
  getRecipeForDish: async (token, dishId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/food-cost/recipe/${dishId}`);
    return response.data;
  },
  saveRecipe: async (token, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/food-cost/recipes', data);
    return response.data;
  },
  deleteRecipe: async (token, dishId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/food-cost/recipes/${dishId}`);
    return response.data;
  },
  getFoodCostDashboard: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/food-cost/dashboard/${restaurantId}`);
    return response.data;
  },

  // Dish Sales
  recordDishSales: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/restaurants/${restaurantId}/dish-sales`, data);
    return response.data;
  },
  getDishSales: async (token, restaurantId, date) => {
    const instance = createApiInstance(token);
    const params = date ? { date } : {};
    const response = await instance.get(`/restaurants/${restaurantId}/dish-sales`, { params });
    return response.data;
  },

  // Invoice scanning
  scanInvoice: async (token, restaurantId, file, lang = '') => {
    const formData = new FormData();
    formData.append('file', file);
    const instance = axios.create({
      baseURL: `/api`,
      timeout: 120000,
      headers: { Authorization: `Bearer ${token}` },
    });
    const response = await instance.post(`/invoices/scan/${restaurantId}?lang=${lang}`, formData);
    return response.data;
  },
  confirmInvoice: async (token, restaurantId, data) => {
    const instance = createApiInstance(token, 30000);
    const response = await instance.post(`/invoices/confirm/${restaurantId}`, data);
    return response.data;
  },
  recalculateDiscountedPrices: async (token, restaurantId) => {
    const instance = createApiInstance(token, 60000);
    const response = await instance.post(`/invoices/recalculate-prices/${restaurantId}`);
    return response.data;
  },
  getInvoices: async (token, restaurantId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/invoices/${restaurantId}`);
    return response.data;
  },
  deleteInvoice: async (token, restaurantId, invoiceId) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/invoices/${restaurantId}/${invoiceId}`);
    return response.data;
  },
  getPriceHistory: async (token, restaurantId, ingredientId = null) => {
    const instance = createApiInstance(token);
    let url = `/invoices/price-history/${restaurantId}`;
    if (ingredientId) url += `?ingredient_id=${ingredientId}`;
    const response = await instance.get(url);
    return response.data;
  },

  // Expense invoice scanning
  scanExpenseInvoice: async (token, restaurantId, file, lang = '') => {
    const formData = new FormData();
    formData.append('file', file);
    const instance = axios.create({
      baseURL: `/api`,
      timeout: 120000,
      headers: { Authorization: `Bearer ${token}` },
    });
    const response = await instance.post(`/expenses/scan-invoice/${restaurantId}?lang=${lang}`, formData);
    return response.data;
  },
  confirmExpenseInvoice: async (token, restaurantId, data) => {
    const instance = createApiInstance(token, 30000);
    const response = await instance.post(`/expenses/confirm-invoice/${restaurantId}`, data);
    return response.data;
  },

  // Receipt scanning for daily tracker
  scanReceipt: async (token, restaurantId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const instance = axios.create({
      baseURL: `/api`,
      timeout: 120000,
      headers: { Authorization: `Bearer ${token}` },
    });
    const response = await instance.post(`/daily-tracker/scan-receipt/${restaurantId}`, formData);
    return response.data;
  },

  // Monthly Food Cost
  getMonthlyFoodCost: async (token, restaurantId, month) => {
    const instance = createApiInstance(token);
    const params = month ? { month } : {};
    const response = await instance.get(`/food-cost-monthly/${restaurantId}`, { params });
    return response.data;
  },
  saveMonthlyInventory: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/food-cost-monthly/inventory/${restaurantId}`, data);
    return response.data;
  },
  getTheoreticalFoodCost: async (token, restaurantId, month) => {
    const instance = createApiInstance(token);
    const params = month ? { month } : {};
    const response = await instance.get(`/food-cost-monthly/theoretical/${restaurantId}`, { params });
    return response.data;
  },
  getInventoryDiscrepancies: async (token, restaurantId, month) => {
    const instance = createApiInstance(token);
    const params = month ? { month } : {};
    const response = await instance.get(`/food-cost-monthly/discrepancies/${restaurantId}`, { params });
    return response.data;
  },

  // PDF Report Downloads
  downloadMonthlyFCReport: async (token, restaurantId, month) => {
    const instance = createApiInstance(token);
    const params = month ? { month } : {};
    const response = await instance.get(`/food-cost-monthly/report-pdf/${restaurantId}`, {
      params, responseType: 'blob'
    });
    return response.data;
  },
  downloadStressTestReport: async (token, restaurantId, input, result) => {
    const instance = createApiInstance(token);
    const response = await instance.post(
      `/restaurants/${restaurantId}/stress-test/report-pdf`,
      { input, result },
      { responseType: 'blob' }
    );
    return response.data;
  },

  // P&L (Conto Economico)
  getPnl: async (token, restaurantId, month) => {
    const instance = createApiInstance(token);
    const params = month && month !== 'all' ? { month } : {};
    const response = await instance.get(`/restaurants/${restaurantId}/pnl`, { params });
    return response.data;
  },

  // Payments (Stripe)
  createCheckoutSession: async (token, planId, billingCycle) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/payments/checkout', {
      plan_id: planId,
      billing_cycle: billingCycle,
      origin_url: window.location.origin,
    });
    return response.data;
  },

  getCheckoutStatus: async (token, sessionId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/payments/checkout/status/${sessionId}`);
    return response.data;
  },

  getPaymentHistory: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/payments/history');
    return response.data;
  },

  // Food Cost per Area
  getFoodCostByArea: async (token, restaurantId, month) => {
    const instance = createApiInstance(token);
    const params = month ? { month } : {};
    const response = await instance.get(`/food-cost-area/${restaurantId}`, { params });
    return response.data;
  },
  saveAreaInventory: async (token, restaurantId, data) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/food-cost-area/inventory/${restaurantId}`, data);
    return response.data;
  },

  // Credit Notes
  getCreditNotes: async (token, restaurantId, month) => {
    const instance = createApiInstance(token);
    const params = month ? { month } : {};
    const response = await instance.get(`/credit-notes/${restaurantId}`, { params });
    return response.data;
  },

  // Prospects
  getProspects: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/prospects/list');
    return response.data;
  },
  getProspectStats: async (token) => {
    const instance = createApiInstance(token);
    const response = await instance.get('/prospects/stats');
    return response.data;
  },
  updateProspect: async (token, id, data) => {
    const instance = createApiInstance(token);
    const response = await instance.put(`/prospects/${id}`, data);
    return response.data;
  },
  deleteProspect: async (token, id) => {
    const instance = createApiInstance(token);
    const response = await instance.delete(`/prospects/${id}`);
    return response.data;
  },
  bulkDeleteProspects: async (token, ids) => {
    const instance = createApiInstance(token);
    const response = await instance.delete('/prospects/bulk/delete', { data: { ids } });
    return response.data;
  },
  importProspectsPaste: async (token, text, city, priority, type, checkDuplicates) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/prospects/import-paste', { text, city, priority, type, check_duplicates: checkDuplicates });
    return response.data;
  },
  importProspectsCsv: async (token, csvText, checkDuplicates) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/prospects/import-csv', { csv_text: csvText, check_duplicates: checkDuplicates });
    return response.data;
  },
  searchCityProspects: async (token, city, query) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/prospects/search-city', { city, query });
    return response.data;
  },
  saveProspectsFromSearch: async (token, items, city) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/prospects/save-from-search', { items, city });
    return response.data;
  },
  sendProspectCampaign: async (token, prospectIds, campaignType, subject, htmlBody, leadMagnetType) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/prospects/send-campaign', {
      prospect_ids: prospectIds, campaign_type: campaignType, subject, html_body: htmlBody, lead_magnet_type: leadMagnetType
    });
    return response.data;
  },
  aiSuggestEmail: async (token, prospect, lang, contactStage, languages, sofiaSuggestions, currentEmail) => {
    const instance = createApiInstance(token);
    const contactHistory = prospect.contact_history || [];
    const payload = {
      name: prospect.name, type: prospect.type, priority: prospect.priority,
      key_outlets: prospect.key_outlets, why_strategic: prospect.why_strategic,
      contact_person: prospect.contact_person || '', contact_role: prospect.contact_role || '',
      lang, contact_stage: contactStage || 'primo_contatto', languages: languages || [lang],
      contact_count: contactHistory.length,
      sofia_suggestions: sofiaSuggestions || [],
    };
    if (currentEmail) payload.current_email = currentEmail;
    const response = await instance.post('/prospects/ai-suggest-email', payload);
    return response.data;
  },
  aiAnalyzeProspect: async (token, prospect, lang) => {
    const instance = createApiInstance(token);
    const response = await instance.post('/prospects/ai-analyze-prospect', {
      name: prospect.name, type: prospect.type, priority: prospect.priority,
      key_outlets: prospect.key_outlets || '', why_strategic: prospect.why_strategic || '',
      contact_person: prospect.contact_person || '', contact_role: prospect.contact_role || '',
      city: prospect.city || '', website: prospect.website || '',
      google_rating: prospect.google_rating, review_count: prospect.review_count,
      contact_history: prospect.contact_history || [], lang: lang || 'it',
    });
    return response.data;
  },
  addContactLog: async (token, prospectId, channel, outcome, notes, newStatus) => {
    const instance = createApiInstance(token);
    const response = await instance.post(`/prospects/${prospectId}/contact-log`, {
      channel, outcome, notes, new_status: newStatus || '',
    });
    return response.data;
  },
  getProspectDetail: async (token, prospectId) => {
    const instance = createApiInstance(token);
    const response = await instance.get(`/prospects/${prospectId}/detail`);
    return response.data;
  },
};

export default api;
